/**
 * 消息类型适配器
 * 将不同类型的消息统一适配为标准格式
 */
export default class MessageTypeAdapter {
  /**
   * 适配消息对象
   * @param {Object} message 原始消息对象
   * @returns {Object} 适配后的消息对象
   */
  static adapt(message) {
    if (!message) {
      return this.createDefaultMessage();
    }
    
    // 根据消息类型选择适配器
    const adapters = {
      'PRIVATE': this.adaptPrivateMessage,
      'GROUP': this.adaptGroupMessage,
      'AGENT': this.adaptAgentMessage,
      'SYSTEM': this.adaptSystemMessage,
      'STREAM': this.adaptStreamMessage,
      'LLM': this.adaptLLMMessage
    };
    
    const adapter = adapters[message.type] || adapters[message.msgType] || this.adaptDefaultMessage;
    return adapter.call(this, message);
  }
  
  /**
   * 创建默认消息对象
   */
  static createDefaultMessage() {
    return {
      content: '',
      format: 'text',
      features: {
        markdown: false,
        katex: false,
        mermaid: false,
        codeHighlight: false
      },
      streaming: false,
      type: 'UNKNOWN'
    };
  }
  
  /**
   * 适配私聊消息
   */
  static adaptPrivateMessage(message) {
    return {
      ...message,
      content: message.content || message.msg || '',
      format: this.detectFormat(message),
      features: this.detectFeatures(message),
      streaming: false,
      type: 'PRIVATE'
    };
  }
  
  /**
   * 适配群聊消息
   */
  static adaptGroupMessage(message) {
    return {
      ...message,
      content: message.content || message.msg || '',
      format: this.detectFormat(message),
      features: this.detectFeatures(message),
      streaming: false,
      type: 'GROUP'
    };
  }
  
  /**
   * 适配智能体消息
   */
  static adaptAgentMessage(message) {
    const content = message.content || message.msg || message.message || '';
    
    return {
      ...message,
      content: content,
      format: 'markdown', // 智能体消息默认使用markdown
      features: {
        markdown: true,
        katex: true,
        mermaid: true,
        codeHighlight: true
      },
      streaming: message.streaming || message.isStreaming || false,
      type: 'AGENT',
      isAgent: true,
      // 智能体特有属性
      agentId: message.agentId || message.agent_id,
      agentName: message.agentName || message.agent_name,
      modelInfo: message.modelInfo || message.model_info
    };
  }
  
  /**
   * 适配系统消息
   */
  static adaptSystemMessage(message) {
    return {
      ...message,
      content: message.content || message.msg || '',
      format: 'text',
      features: {
        markdown: false,
        katex: false,
        mermaid: false,
        codeHighlight: false
      },
      streaming: false,
      type: 'SYSTEM'
    };
  }
  
  /**
   * 适配流式消息
   */
  static adaptStreamMessage(message) {
    return {
      ...message,
      content: message.content || message.msg || '',
      format: 'markdown',
      features: {
        markdown: true,
        katex: true,
        mermaid: true,
        codeHighlight: true
      },
      streaming: true,
      type: 'STREAM'
    };
  }
  
  /**
   * 适配LLM消息
   */
  static adaptLLMMessage(message) {
    return {
      ...message,
      content: message.content || message.text || message.message || '',
      format: 'markdown',
      features: {
        markdown: true,
        katex: true,
        mermaid: true,
        codeHighlight: true
      },
      streaming: message.streaming !== false,
      type: 'LLM',
      // LLM特有属性
      model: message.model,
      temperature: message.temperature,
      maxTokens: message.max_tokens || message.maxTokens,
      completionTokens: message.completion_tokens || message.completionTokens,
      promptTokens: message.prompt_tokens || message.promptTokens
    };
  }
  
  /**
   * 适配默认消息
   */
  static adaptDefaultMessage(message) {
    const content = this.extractContent(message);
    
    return {
      ...message,
      content: content,
      format: this.detectFormat(message),
      features: this.detectFeatures(message),
      streaming: message.streaming || false,
      type: message.type || 'DEFAULT'
    };
  }
  
  /**
   * 提取消息内容
   */
  static extractContent(message) {
    // 尝试从不同字段提取内容
    const contentFields = ['content', 'msg', 'message', 'text', 'body', 'data'];
    
    for (const field of contentFields) {
      if (message[field]) {
        if (typeof message[field] === 'string') {
          return message[field];
        }
        if (typeof message[field] === 'object' && message[field].text) {
          return message[field].text;
        }
      }
    }
    
    return '';
  }
  
  /**
   * 检测消息格式
   */
  static detectFormat(message) {
    // 优先使用消息自带的格式标记
    if (message.format) {
      return message.format;
    }
    
    // 检查消息类型
    if (message.isMarkdown || message.is_markdown) {
      return 'markdown';
    }
    
    // 智能体和LLM消息默认markdown
    if (message.type === 'AGENT' || message.type === 'LLM' || message.isAgent) {
      return 'markdown';
    }
    
    // 检测内容是否包含markdown语法
    const content = this.extractContent(message);
    if (this.hasMarkdownSyntax(content)) {
      return 'markdown';
    }
    
    return 'text';
  }
  
  /**
   * 检测消息特性
   */
  static detectFeatures(message) {
    const content = this.extractContent(message);
    
    // 如果是纯文本，所有特性都关闭
    if (this.detectFormat(message) === 'text') {
      return {
        markdown: false,
        katex: false,
        mermaid: false,
        codeHighlight: false
      };
    }
    
    return {
      markdown: true,
      katex: this.hasLatex(content),
      mermaid: this.hasMermaid(content),
      codeHighlight: this.hasCodeBlock(content)
    };
  }
  
  /**
   * 检测Markdown语法
   */
  static hasMarkdownSyntax(content) {
    if (!content) return false;
    
    const patterns = [
      /^#{1,6}\s/m,           // 标题
      /\*\*[^*]+\*\*/,        // 粗体
      /\*[^*]+\*/,            // 斜体
      /```[\s\S]*?```/,       // 代码块
      /`[^`]+`/,              // 行内代码
      /^\s*[-*+]\s/m,         // 无序列表
      /^\s*\d+\.\s/m,         // 有序列表
      /\[([^\]]+)\]\(([^)]+)\)/, // 链接
      /^\|.*\|$/m,            // 表格
      /^>/m                   // 引用
    ];
    
    return patterns.some(pattern => pattern.test(content));
  }
  
  /**
   * 检测LaTeX公式
   */
  static hasLatex(content) {
    if (!content) return false;
    return /\$[^$]+\$|\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)/.test(content);
  }
  
  /**
   * 检测Mermaid图表
   */
  static hasMermaid(content) {
    if (!content) return false;
    return /```mermaid[\s\S]*?```/.test(content);
  }
  
  /**
   * 检测代码块
   */
  static hasCodeBlock(content) {
    if (!content) return false;
    return /```[\s\S]*?```/.test(content);
  }
  
  /**
   * 合并消息更新（用于流式消息）
   */
  static mergeUpdate(originalMessage, update) {
    const adapted = this.adapt(originalMessage);
    
    // 合并内容
    if (update.content !== undefined) {
      adapted.content = update.append ? adapted.content + update.content : update.content;
    }
    
    // 更新流式状态
    if (update.streaming !== undefined) {
      adapted.streaming = update.streaming;
    }
    
    // 更新其他属性
    return {
      ...adapted,
      ...update,
      content: adapted.content
    };
  }
}